import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getCachedBillingData } from '../data/api';
import { pendingIcon } from '../icons';
import Loading from './shared/Loading';

const BACKUP_INTERVAL_HOURS = 3;
const MAX_BACKUP_ITEMS = 10;
const HOURS_24_IN_MS = 24 * 60 * 60 * 1000;
const BACKUP_INTERVAL_IN_MS = BACKUP_INTERVAL_HOURS * 60 * 60 * 1000;
const BACKUP_SCHEDULE_START_HOUR_UTC = 1;
const MOBILE_BREAKPOINT_PX = 900;
const PLACEHOLDER_MIN_HEIGHT_PX = 260;
const MOBILE_HEADER_VERTICAL_SPACING_PX = 11;
const BLOG_PLACEHOLDER_TEXT = 'Backups are being created';
const ERROR_PLACEHOLDER_TEXT = 'Backups are unavailable right now';

const formatBackupDate = (timestamp) => {
    const localeOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    };

    return new Date(timestamp).toLocaleString('en-GB', localeOptions);
};

const formatBackupTime = (timestamp) => {
    const localeOptions = {
        hour: 'numeric',
        hour12: true,
        timeZone: 'UTC',
    };

    const formattedTime = new Date(timestamp)
        .toLocaleString('en-GB', localeOptions)
        .replace(/\s+/g, '')
        .toLowerCase();

    return `${formattedTime} UTC`;
};

const getLatestBackupTimestamp = (nowTimestamp) => {
    const now = new Date(nowTimestamp);
    const firstBackupToday = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        BACKUP_SCHEDULE_START_HOUR_UTC,
        0,
        0,
        0,
    );

    if (nowTimestamp < firstBackupToday) {
        return firstBackupToday - BACKUP_INTERVAL_IN_MS;
    }

    const elapsedSinceFirstBackup = nowTimestamp - firstBackupToday;
    const completeIntervals = Math.floor(elapsedSinceFirstBackup / BACKUP_INTERVAL_IN_MS);

    return firstBackupToday + completeIntervals * BACKUP_INTERVAL_IN_MS;
};

const getBackupItems = () => {
    const now = Date.now();
    const latestBackupTimestamp = getLatestBackupTimestamp(now);

    return Array.from({ length: MAX_BACKUP_ITEMS }, (_, index) => {
        const timestamp = latestBackupTimestamp - index * BACKUP_INTERVAL_IN_MS;

        return {
            id: index + 1,
            timestamp,
        };
    });
};

const getSiteDomain = (billingData) => {
    const currentSite = billingData?.currentSite;
    const blog = billingData?.blogs?.[0];
    const domainFromFields = currentSite?.external_domain || blog?.external_domain;
    const url = currentSite?.url || blog?.url;

    if (domainFromFields) {
        return domainFromFields;
    }

    if (url) {
        try {
            return new URL(url).hostname;
        } catch (error) {
            return String(url)
                .replace(/^https?:\/\//i, '')
                .replace(/\/.*$/, '');
        }
    }

    if (currentSite?.subdomain && currentSite?.pro_domain) {
        return `${currentSite.subdomain}.${currentSite.pro_domain}`;
    }

    return 'site.domain';
};

function Backups({ showNav, updateTitle }) {
    const [loading, setLoading] = useState(true);
    const [showPlaceholder, setShowPlaceholder] = useState(false);
    const [placeholderText, setPlaceholderText] = useState(BLOG_PLACEHOLDER_TEXT);
    const [backupItems, setBackupItems] = useState([]);
    const [siteDomain, setSiteDomain] = useState('site.domain');
    const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth <= MOBILE_BREAKPOINT_PX);

    useEffect(() => {
        let isCancelled = false;

        const fetchData = async () => {
            if (showNav) {
                showNav(true);
            }
            updateTitle('');

            const billingData = await getCachedBillingData();

            if (isCancelled) {
                return;
            }

            if (!billingData || billingData.error) {
                setShowPlaceholder(true);
                setPlaceholderText(ERROR_PLACEHOLDER_TEXT);
                setBackupItems([]);
                setLoading(false);
                return;
            }

            const blogCreatedAt =
                billingData?.blogs?.[0]?.created_at || billingData?.currentSite?.created_at;
            const blogCreatedAtTimestamp = blogCreatedAt ? new Date(blogCreatedAt).getTime() : null;
            const isBlogLessThan24HoursOld =
                blogCreatedAtTimestamp !== null &&
                Date.now() - blogCreatedAtTimestamp < HOURS_24_IN_MS;

            setSiteDomain(getSiteDomain(billingData));
            setShowPlaceholder(!!isBlogLessThan24HoursOld);
            setPlaceholderText(
                isBlogLessThan24HoursOld ? BLOG_PLACEHOLDER_TEXT : ERROR_PLACEHOLDER_TEXT,
            );
            setBackupItems(isBlogLessThan24HoursOld ? [] : getBackupItems());
            setLoading(false);
        };

        fetchData().catch(() => {
            if (!isCancelled) {
                setShowPlaceholder(true);
                setPlaceholderText(ERROR_PLACEHOLDER_TEXT);
                setBackupItems([]);
                setLoading(false);
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [showNav, updateTitle]);

    useEffect(() => {
        const handleResize = () => {
            setIsNarrowScreen(window.innerWidth <= MOBILE_BREAKPOINT_PX);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (loading) {
        return <Loading />;
    }

    const backupRequestSubject = `Site backup request: ${siteDomain}`;
    const backupRequestMailto = `mailto:support@ghost.org?subject=${encodeURIComponent(backupRequestSubject)}`;

    return (
        <main className="main">
            <div className="box" style={{ padding: 0, overflow: 'hidden' }}>
                <div data-testid="backups-grid">
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: isNarrowScreen ? '1fr' : 'minmax(0, 1fr) auto',
                            alignItems: 'start',
                            gap: isNarrowScreen ? 0 : '16px',
                            padding: '18px 20px',
                            borderBottom: '1px solid #ebebeb',
                        }}
                    >
                        <div style={{ minWidth: 0 }}>
                            <div
                                className="staticfield-label"
                                style={{
                                    marginBottom: 0,
                                    fontSize: '2.2rem',
                                    letterSpacing: '-.01em',
                                    fontWeight: 700,
                                }}
                            >
                                Backups
                            </div>
                            <div
                                style={{
                                    fontWeight: 400,
                                    marginTop: `${MOBILE_HEADER_VERTICAL_SPACING_PX}px`,
                                }}
                            >
                                Your site is automatically backed up every 3 hours, contact support
                                to download or restore a backup.
                            </div>
                        </div>
                        <div
                            style={{
                                width: isNarrowScreen ? '100%' : '18rem',
                                maxWidth: '100%',
                                marginLeft: isNarrowScreen ? 0 : 'auto',
                                marginTop: isNarrowScreen
                                    ? `${MOBILE_HEADER_VERTICAL_SPACING_PX}px`
                                    : 0,
                            }}
                        >
                            <a
                                className="gh-btn gh-btn-black gh-btn-block"
                                href={backupRequestMailto}
                                target="_top"
                                rel="noopener noreferrer"
                            >
                                Request backup
                            </a>
                        </div>
                    </div>

                    {showPlaceholder ? (
                        <div
                            style={{
                                minHeight: `${PLACEHOLDER_MIN_HEIGHT_PX}px`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '18px 20px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '14px',
                                    textAlign: 'center',
                                }}
                            >
                                <img
                                    src={pendingIcon}
                                    alt=""
                                    style={{ width: '26px', height: '26px', opacity: 0.7 }}
                                />
                                <span style={{ opacity: 0.7 }}>{placeholderText}</span>
                            </div>
                        </div>
                    ) : (
                        backupItems.map((backup, index) => {
                            return (
                                <div
                                    key={backup.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                                        gap: '16px',
                                        alignItems: 'center',
                                        backgroundColor: index % 2 === 0 ? '#f7f7f7' : '#ffffff',
                                        padding: '16px 20px',
                                    }}
                                >
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontWeight: 600 }}>{siteDomain}</div>
                                        <div style={{ opacity: 0.5 }}>Full site backup</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 600 }}>
                                            {formatBackupDate(backup.timestamp)}
                                        </div>
                                        <time
                                            data-testid="backup-time"
                                            style={{ opacity: 0.5, fontFamily: 'monospace' }}
                                            dateTime={new Date(backup.timestamp).toISOString()}
                                        >
                                            {formatBackupTime(backup.timestamp)}
                                        </time>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </main>
    );
}

Backups.propTypes = {
    showNav: PropTypes.func,
    updateTitle: PropTypes.func,
};

export default Backups;
